import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import API from "../api/axios";
import { NAVY, ACCENT } from "../theme";

const roles = [
  { value: "RECRUITER", label: "Recruiter" },
  { value: "STAFF", label: "Staff" },
];

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "RECRUITER",
  });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setError("");
    setSuccess("");
    if (!form.name || !form.email || !form.password) {
      setError("Name, email and password are required.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await API.post("/auth/register", form);
      setSuccess("Account created successfully! Redirecting to login…");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Could not create the account. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 920,
          minHeight: { md: 560 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        {/* Brand panel */}
        <Box
          sx={{
            flex: 1,
            bgcolor: NAVY,
            color: "#fff",
            p: { xs: 3, md: 4.5 },
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: ACCENT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              TN
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.1 }}
              >
                TechNext
              </Typography>
              <Typography
                sx={{ fontSize: 10, color: "#8fa3c4", letterSpacing: 0.5 }}
              >
                RECRUITMENT PORTAL
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: "auto" }}>
            <Typography
              sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.25 }}
            >
              Join the team.
              <br />
              Start hiring.
            </Typography>
            <Typography
              sx={{
                fontSize: 13.5,
                color: "#a8b4c8",
                mt: 1.75,
                lineHeight: 1.6,
              }}
            >
              Create your account to manage positions and candidates in one
              place.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, color: "#6b7a93", mt: 3.5 }}>
            © 2026 TechNext
          </Typography>
        </Box>

        {/* Form panel */}
        <Box
          sx={{
            flex: 1.1,
            p: { xs: 3, sm: 5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.25,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1,
                bgcolor: ACCENT,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              TN
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
              TechNext
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Create your account
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 3 }}>
            Set up access to the recruitment portal
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            label="Full name"
            value={form.name}
            onChange={set("name")}
            onKeyDown={onKeyDown}
            sx={{ mb: 2 }}
            placeholder="Jane Doe"
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={set("email")}
            onKeyDown={onKeyDown}
            sx={{ mb: 2 }}
            placeholder="you@company.com"
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={set("phone")}
            onKeyDown={onKeyDown}
            sx={{ mb: 2 }}
            placeholder="Optional"
          />
          <TextField
            label="Password"
            type={showPw ? "text" : "password"}
            value={form.password}
            onChange={set("password")}
            onKeyDown={onKeyDown}
            sx={{ mb: 2 }}
            placeholder="At least 6 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPw(!showPw)}
                    edge="end"
                    size="small"
                    aria-label="toggle password"
                  >
                    {showPw ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Role"
            value={form.role}
            onChange={set("role")}
            sx={{ mb: 3 }}
          >
            {roles.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            size="large"
            onClick={submit}
            disabled={loading}
            sx={{ height: 46, fontSize: 15 }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Create account"
            )}
          </Button>

          <Typography
            sx={{
              mt: 2.5,
              fontSize: 13,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            Already have an account?{" "}
            <Link
              component={RouterLink}
              to="/login"
              sx={{ fontWeight: 600, color: ACCENT }}
              underline="hover"
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
